import { Genders } from '@chiroup/core/enums/Genders.enum';
import { Patient } from '@chiroup/core/types/Patient.type';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import requestService from '../../services/request.service';

const verbiage = {
  en: {
    title: 'About you',
    description: 'A few quick details so the clinic can keep your record up to date.',
    fname: 'First name',
    lname: 'Last name',
    pname: 'Preferred name (optional)',
    dob: 'Date of birth',
    sex: 'Sex',
    sexMale: 'Male',
    sexFemale: 'Female',
    sexOther: 'Other',
    sexUnknown: 'Prefer not to say',
    address1: 'Street address',
    address2: 'Apt / suite (optional)',
    city: 'City',
    state: 'State / region',
    zip: 'ZIP / postal code',
    phone: 'Phone',
    email: 'Email',
    submit: 'Save and continue',
    submitting: 'Saving…',
    submittedTitle: 'Information saved',
    submittedDetail: 'Thanks for keeping us up to date.',
    continue: 'Continue',
    errorTitle: 'Something went wrong',
    errorDetail: 'Please try again in a moment.',
  },
  es: {
    title: 'Sobre usted',
    description: 'Algunos detalles rápidos para que la clínica tenga su información al día.',
    fname: 'Nombre',
    lname: 'Apellido',
    pname: 'Nombre preferido (opcional)',
    dob: 'Fecha de nacimiento',
    sex: 'Sexo',
    sexMale: 'Masculino',
    sexFemale: 'Femenino',
    sexOther: 'Otro',
    sexUnknown: 'Prefiero no decirlo',
    address1: 'Dirección',
    address2: 'Apto / unidad (opcional)',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código postal',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    submit: 'Guardar y continuar',
    submitting: 'Guardando…',
    submittedTitle: 'Información guardada',
    submittedDetail: 'Gracias por mantenerse al día.',
    continue: 'Continuar',
    errorTitle: 'Algo salió mal',
    errorDetail: 'Por favor inténtelo de nuevo en unos momentos.',
  },
};

type Props = {
  requestId: string;
  validationCode: string | number;
  language: 'en' | 'es';
  primaryColor: string;
  prMap?: { [key: string]: string };
  alreadyComplete?: boolean;
  onComplete: () => void;
};

type Status = 'ready' | 'submitting' | 'submitted' | 'error';

const StepBasicInformation: React.FC<Props> = ({
  requestId,
  validationCode,
  language,
  primaryColor,
  prMap,
  alreadyComplete,
  onComplete,
}) => {
  const v = verbiage[language];
  const [status, setStatus] = useState<Status>(alreadyComplete ? 'submitted' : 'ready');

  const [fname, setFname] = useState('');
  const [lname, setLname] = useState('');
  const [pname, setPname] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [sex, setSex] = useState<Genders>(Genders.notDisclosed);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (status === 'submitting') return;

    setStatus('submitting');

    try {
      const data: Partial<Patient> = {
        fname: fname.trim() || undefined,
        lname: lname.trim() || undefined,
        pname: pname.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
        gender: sex,
        address1: address1.trim() || undefined,
        address2: address2.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip: zip.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      };

      await requestService.submitInformation(requestId, data, validationCode, prMap);
      setStatus('submitted');
    } catch (err) {
      Sentry.captureException(err);
      setStatus('error');
    }
  };

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <div
          className="size-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
          aria-hidden="true"
        >
          <CheckCircleSolid className="size-7" />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {v.submittedTitle}
        </h1>
        <p className="max-w-prose text-sm text-gray-600 dark:text-gray-300">{v.submittedDetail}</p>
        <button
          type="button"
          onClick={onComplete}
          className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {v.continue}
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="portal-card p-6 text-center">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.errorTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.errorDetail}</p>
      </div>
    );
  }

  const inputCls =
    'w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent';
  const ringStyle = { ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties;

  return (
    <form onSubmit={handleSubmit} className="portal-card p-5 sm:p-6 flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {v.title}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-prose">{v.description}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={v.fname}>
          <input
            type="text"
            name="fname"
            value={fname}
            onChange={e => setFname(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.lname}>
          <input
            type="text"
            name="lname"
            value={lname}
            onChange={e => setLname(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.pname} className="sm:col-span-2">
          <input
            type="text"
            name="pname"
            value={pname}
            onChange={e => setPname(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.dob}>
          <input
            type="date"
            name="dateOfBirth"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.sex}>
          <div className="inline-grid w-full grid-cols-[1fr_--spacing(8)]">
            <select
              name="sex"
              value={sex}
              onChange={e => setSex(e.target.value as Genders)}
              disabled={status === 'submitting'}
              className={`col-span-full row-start-1 appearance-none pr-8 ${inputCls}`}
              style={ringStyle}
            >
              <option value={Genders.notDisclosed}>{v.sexUnknown}</option>
              <option value={Genders.male}>{v.sexMale}</option>
              <option value={Genders.female}>{v.sexFemale}</option>
              <option value={Genders.other}>{v.sexOther}</option>
            </select>
            <svg
              viewBox="0 0 8 5"
              width="8"
              height="5"
              fill="none"
              className="pointer-events-none col-start-2 row-start-1 place-self-center"
              aria-hidden="true"
            >
              <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
            </svg>
          </div>
        </Field>
        <Field label={v.address1} className="sm:col-span-2">
          <input
            type="text"
            name="address1"
            value={address1}
            onChange={e => setAddress1(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.address2} className="sm:col-span-2">
          <input
            type="text"
            name="address2"
            value={address2}
            onChange={e => setAddress2(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.city}>
          <input
            type="text"
            name="city"
            value={city}
            onChange={e => setCity(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.state}>
          <input
            type="text"
            name="state"
            value={state}
            onChange={e => setState(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.zip}>
          <input
            type="text"
            name="zip"
            value={zip}
            onChange={e => setZip(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.phone}>
          <input
            type="tel"
            name="phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
        <Field label={v.email} className="sm:col-span-2">
          <input
            type="email"
            name="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={status === 'submitting'}
            className={inputCls}
            style={ringStyle}
          />
        </Field>
      </div>

      <div className="flex items-center justify-end pt-2 border-t border-gray-950/5 dark:border-white/10">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {status === 'submitting' ? v.submitting : v.submit}
        </button>
      </div>
    </form>
  );
};

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className,
  children,
}) => (
  <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {label}
    </span>
    {children}
  </label>
);

export default StepBasicInformation;
